import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt, isDash, simpleValueValidation, formatNumber } from '@miq/utils';
import { DataObject, ReportRecord } from '../../../../models/report.model';
import { DisplayFnArgumentsInfo, LovValues, TemplateFieldsDisplayFn } from '../../../../models/template.model';
import { sortAttributesFields } from '../../utils/argument-sorting';
import {
  displayFnWrapper,
  mapAndFormatDynamicValue,
  displayFnArrayWraperUnique,
} from '../../utils/dynamic-data-retrievers';
import moment from 'moment';
import { Observable, of, switchMap } from 'rxjs';

function mapUsageArgumentToPrimary(arg: DisplayFnArgumentsInfo): DisplayFnArgumentsInfo {
  let mappedArgumentObj = arg;
  if (arg.field != null && arg.field.includes('usages')) {
    const field = arg.field.split('usages/').join(`usages[primary_usage_f=true].`);
    mappedArgumentObj = { ...mappedArgumentObj, field };
  }
  return mappedArgumentObj;
}

const BLANK_SPACE = ' ';

export const displayFnMap: TemplateFieldsDisplayFn = {
  628: {
    // 628 - Property Reference
    displayFn: displayFnWrapper,
  },
  629: {
    //    629 - Address
    displayFn: (record: ReportRecord, options, ...args) => {
      const { displayFnArgumentsOrder = [850, 851, 854] } = options;
      const finalArgs = sortAttributesFields(args, displayFnArgumentsOrder);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy('\n', ...values.filter(value => simpleValueValidation(value) && !isDash(value as string)));
    },
  },
  630: {
    // 630 - Zip Code
    displayFn: displayFnWrapper,
  },
  631: {
    // 631 - Market
    displayFn: (record: ReportRecord, _options, marketArgs, submarketArgs, districtArgs, neighborhoodArgs) => {
      const market = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(marketArgs));
      const submarket = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(submarketArgs));
      const district = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(districtArgs));
      const neighborhood = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(neighborhoodArgs));

      if (simpleValueValidation(neighborhood) && !isDash(neighborhood as string)) {
        return neighborhood;
      }
      if (simpleValueValidation(district) && !isDash(district as string)) {
        return district;
      }
      if (simpleValueValidation(submarket) && !isDash(submarket as string)) {
        return submarket;
      }
      return market;
    },
  },
  632: {
    // 632 - Market Group
    // The logic for this field is to display the 2nd location value that is not
    // present on the market field (631). This means that if the smaller
    // hierarchy attribute has data, we need to display the next one in line
    // (if it has data).
    displayFn: (record: ReportRecord, _options, marketArgs, submarketArgs, districtArgs) => {
      const market = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(marketArgs));
      const submarket = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(submarketArgs));
      const district = mapAndFormatDynamicValue(record, mapUsageArgumentToPrimary(districtArgs));
      const neighborhood = digger(record.data, 'usages[primary_usage_f=true].lig_geography.neighborhood_desc');

      const hasNeighborhoodData = simpleValueValidation(neighborhood) && !isDash(neighborhood as string);
      const hasDistrictData = simpleValueValidation(district) && !isDash(district as string);
      const hasSubmarketData = simpleValueValidation(submarket) && !isDash(submarket as string);

      if (hasNeighborhoodData && hasDistrictData) {
        return district;
      }
      if (hasDistrictData && hasSubmarketData) {
        return submarket;
      }
      return market;
    },
  },
  633: {
    // 633 - Spec
    displayFn: (record: ReportRecord, _, arg) => {
      const lov$ = arg.lovValues ?? (of([]) as Observable<LovValues[]>);
      return lov$.pipe(
        switchMap(classLOV => {
          const lovKeyValue = mapAndFormatDynamicValue(record, arg, classLOV);
          const lovValue = classLOV.find(a => a.key === lovKeyValue);
          return lovValue ? lovValue.value : BLANK_SPACE;
        })
      );
    },
  },
  634: {
    // floor_suite.suite_number, floor_suite.floor_number
    displayFn: (record: ReportRecord, _options, suite, floor) => {
      const florSuite = (record.data as any[]).map(availability => ({
        suite: digger(availability, suite.field),
        floor: digger(availability, floor.field),
      }));
      const result = florSuite.map(floorSuite => {
        if (!floorSuite.floor && !floorSuite.suite) {
          return BLANK_SPACE;
        } else {
          return `${floorSuite.floor ?? BLANK_SPACE} ${floorSuite.suite ?? BLANK_SPACE}`.trim();
        }
      });
      const uniqueResults = [...new Set(result)];
      return uniqueResults.join('\n');
    },
  },
  635: {
    // Floor Split [Availability]
    displayFn: (record: ReportRecord, _options, _arg) => {
      const floorSplitVal = digger(record, 'data/available_space');
      const formattedSplitVal = floorSplitVal.map(floorSplit => floorSplit);
      return formattedSplitVal != null ? formatNumber(formattedSplitVal, undefined, '.0-2') : BLANK_SPACE;
    },
  },
  636: {
    // Status [Availability]
    displayFn: (record: ReportRecord, _options, _arg) => {
      return displayFnArrayWraperUnique(record, _options, _arg);
    },
  },
  637: {
    // Total Available Space not able to find id
    displayFn: (record: ReportRecord, _options, arg) => {
      return displayFnArrayWraperUnique(record, _options, arg);
    },
  },
  638: {
    displayFn: (record: ReportRecord, _options, _arg) => {
      // Tenure - available_space_condition_type_desc
      return displayFnArrayWraperUnique(record, _options, _arg);
    },
  },
  639: {
    displayFn: (record: ReportRecord, _options, _arg) => {
      // Lease Type - ref_lease_type_desc
      return displayFnArrayWraperUnique(record, _options, _arg);
    },
  },
  640: {
    displayFn: (record: ReportRecord, _options, expireDate) => {
      if (!expireDate.field) return '';

      const dates = (record.data as DataObject[]).map(data => data[expireDate.field]);

      function getDateFormatted(subLeaseExpireDate: moment.Moment, now: moment.Moment): string {
        const diffDuration = moment.duration(subLeaseExpireDate.diff(now));
        if (diffDuration.days() >= 15) {
          diffDuration.add(1, 'month');
        }
        const years = diffDuration.years();
        const months = diffDuration.months();
        return `${years} years ${months} months`;
      }

      const datesFormmated = dates.map(date => {
        if (!date) {
          return '';
        }
        const now = moment();
        const subLeaseExpireDate = moment(date);
        if (!subLeaseExpireDate.isValid()) {
          return defaultTxt;
        }
        return getDateFormatted(subLeaseExpireDate, now);
      });

      const unique = [...new Set(datesFormmated)];
      return unique.join('\n');
    },
  },
  641: {
    displayFn: (record: ReportRecord, _options, expireDate) => {
      if (!expireDate.field) {
        return BLANK_SPACE;
      }

      const dates = (record.data as DataObject[]).map(data => data[expireDate.field]);
      const allEqual = dates.every(v => v === dates[0]);
      const checkedForUnique = allEqual ? Array.from(new Set(dates)) : dates;
      const datesFormmated = checkedForUnique.map(date => {
        const momentDate = moment(date);

        if (!momentDate.isValid()) {
          return BLANK_SPACE;
        }
        return momentDate.format('DD-MM-YYYY');
      });

      return datesFormmated.join('\n');
    },
  },
  642: {
    // Rent (£psf)
    displayFn: (record: ReportRecord, _, askingRate) => {
      const arg = { ...askingRate, uomArgument: null };
      const rentVal = mapAndFormatDynamicValue(record, arg);
      if (Array.isArray(rentVal)) {
        return rentVal.map(res => res.replace('-', BLANK_SPACE))[0];
      }
      return !isDash(rentVal) ? rentVal : BLANK_SPACE;
    },
  },
  643: {
    // Rates (£psf)
    displayFn: (record: ReportRecord, _, arg) => {
      const argNoUom = { ...arg, uomArgument: null };
      return displayFnArrayWraperUnique(record, _, argNoUom);
    },
  },
  644: {
    // Service Charge (£psf)
    displayFn: (record: ReportRecord, _, arg) => {
      const argNoUom = { ...arg, uomArgument: null };
      const serviceChargeVal = mapAndFormatDynamicValue(record, argNoUom);
      if (Array.isArray(serviceChargeVal)) {
        return serviceChargeVal.map(res => res.replace('-', BLANK_SPACE))[0];
      }
      return !isDash(serviceChargeVal) ? serviceChargeVal : BLANK_SPACE;
    },
  },
  645: {
    // // Price - asking_price_for_sale
    displayFn: (record: ReportRecord, _, arg) => {
      const argNoUom = { ...arg, uomArgument: null };
      return displayFnArrayWraperUnique(record, _, argNoUom);
    },
  },
  646: {
    // Quality of Space - ref_space_condition_type_desc
    displayFn: (record: ReportRecord, _, arg) => {
      return displayFnArrayWraperUnique(record, _, arg);
    },
  },

  647: {
    displayFn: (record: ReportRecord, _options, _company, _contact, _phone) => {
      function getFormatedContacts(availability: any): string[] {
        const companyContacts = digger(availability, 'company_contact_role_addresses');
        let result = [];
        if (Array.isArray(companyContacts)) {
          for (let contact of companyContacts) {
            if (contact.contact_id) {
              const contactData = [
                contact.company_name,
                contact.contact_display_name,
                contact.full_phone_number,
              ].filter(data => !!data);
              result.push(contactData.join(' '));
            }
          }
        }
        return result;
      }

      const contacts = (record.data as any[]).map(availability => {
        return getFormatedContacts(availability);
      });

      // removing duplicates contacts
      const flatArray = [].concat(...contacts);
      const uniqueContacts = [...new Set(flatArray)];
      return uniqueContacts.join('\n');
    },
  },
};
